import { createContext, useContext } from 'react';

/**
 * Bascule de thème dark/light « L'Arcanum » (§3.9).
 *
 * Thème sombre = défaut de marque (aucun attribut sur <html>) ; thème clair =
 * activé par `data-theme="light"` posé sur `document.documentElement` (le bloc
 * [data-theme='light'] de tokens.css fournit la surcharge runtime).
 *
 * Règle `prefers-color-scheme` au premier chargement (décision tranchée, plan
 * bascule-theme §2) — implémentée ICI en TS (source de vérité testée) ET
 * transcrite en JS inline dans index.html (anti-flash). Garder synchronisées.
 *
 * 1. Choix persisté prioritaire : `localStorage['mada-talk-theme']` == 'light'
 *    ou 'dark' → c'est ce choix qui s'applique (l'utilisateur a tranché).
 * 2. Premier chargement SANS choix persisté : le système n'est honoré que s'il
 *    exprime explicitement `prefers-color-scheme: light` → thème light ; sinon
 *    (système dark ou préférence absente) → dark (défaut de marque).
 * 3. Pas de suivi continu du système (aucun listener `change` sur matchMedia) :
 *    le thème ne change ensuite que via le toggle (qui persiste).
 * 4. localStorage indisponible (privé/quota) : try/catch silencieux → fallback
 *    dark.
 */

export type Theme = 'dark' | 'light';

export const THEME_STORAGE_KEY = 'mada-talk-theme';

export interface ThemeContextValue {
  readonly theme: Theme;
  readonly toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export { ThemeContext };

/**
 * Lecture du choix persisté.
 * Garde jsdom / mode privé : `typeof window` + try/catch (aucun accès direct
 * non protégé), même pattern que use-media-query.ts.
 */
export function readStoredTheme(
  storage: Pick<Storage, 'getItem'> | null | undefined,
): Theme | null {
  if (!storage) {
    return null;
  }
  try {
    const raw = storage.getItem(THEME_STORAGE_KEY);
    return raw === 'light' || raw === 'dark' ? raw : null;
  } catch {
    return null;
  }
}

/**
 * Le système exprime-t-il explicitement `prefers-color-scheme: light` ?
 * Retourne false si matchMedia est absent (jsdom / environnement restreint).
 */
export function prefersLight(
  matchMedia: ((query: string) => { readonly matches: boolean }) | null | undefined,
): boolean {
  if (typeof matchMedia !== 'function') {
    return false;
  }
  try {
    return matchMedia('(prefers-color-scheme: light)').matches;
  } catch {
    return false;
  }
}

export interface ResolveInitialThemeOptions {
  readonly storage?: Pick<Storage, 'getItem'> | null;
  readonly matchMedia?: ((query: string) => { readonly matches: boolean }) | null;
}

/**
 * Résolution du thème initial — fonction pure testable sans DOM.
 * Sans options explicites, lit le localStorage et matchMedia du navigateur
 * (accès protégés : `typeof window` + try/catch).
 */
export function resolveInitialTheme(options: ResolveInitialThemeOptions = {}): Theme {
  const storage = options.storage ?? (typeof window === 'undefined' ? null : window.localStorage);
  const matchMedia =
    options.matchMedia ??
    (typeof window === 'undefined' || typeof window.matchMedia !== 'function'
      ? null
      : window.matchMedia.bind(window));

  const stored = readStoredTheme(storage);
  if (stored) {
    return stored;
  }
  return prefersLight(matchMedia) ? 'light' : 'dark';
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme doit être utilisé dans <ThemeProvider>.');
  }
  return context;
}
